import React, { useState, useEffect } from "react";
import { AtSign, ArrowRight, Eye, EyeOff, Lock, ShieldCheck, X } from "lucide-react";
import { authService, companyService } from "../../services/api";
import { getAuthItem, setAuthItem, setAuthJson } from "../../utils/authStorage";
import paytiempLogo from "../../assets/paytiemp-logo.png";
import {
  Icon3dAttendance,
  Icon3dHiring,
  Icon3dMySection,
  Icon3dPayroll,
  Icon3dProfile,
} from "../Admin/sidebar/Sidebar3dIcons";

const FEATURES = [
  {
    title: "Onboarding",
    blurb: "Start the employee journey",
    Icon: Icon3dHiring,
  },
  {
    title: "Attendance",
    blurb: "Track and report presence",
    Icon: Icon3dAttendance,
  },
  {
    title: "Payroll",
    blurb: "Process and disburse salaries",
    Icon: Icon3dPayroll,
  },
  {
    title: "People",
    blurb: "Profiles, transfers and exits",
    Icon: Icon3dProfile,
  },
  {
    title: "Teams",
    blurb: "Control and monitor operations",
    Icon: Icon3dMySection,
  },
];

const STATS = [
  ["10K+", "Active Users"],
  ["99.9%", "Uptime"],
  ["24/7", "Support"],
];

export default function LoginPage({ onLogin }) {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpCode, setOtpCode] = useState("");

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const storedCompanyData = getAuthItem("companyData");
        if (storedCompanyData) {
          setCompanyInfo(JSON.parse(storedCompanyData));
          return;
        }
        const data = await companyService.getCompanyByDomain();
        if (data?.success && data?.data?.company) {
          setCompanyInfo(data.data.company);
        }
      } catch (fetchError) {
        console.error("Failed to fetch company info:", fetchError);
      }
    };

    fetchCompanyInfo();
  }, []);

  const finishLogin = (response) => {
    const resolvedTypeFromResponse = String(
      response.userType || response.user?.userType || ""
    ).toLowerCase();
    const backendUserType =
      resolvedTypeFromResponse === "admin" ||
      resolvedTypeFromResponse === "super_admin" ||
      response.user?.role === "admin" ||
      response.user?.role === "super_admin"
        ? "admin"
        : "employee";

    setAuthItem("isAuthenticated", "true");
    setAuthItem("userType", backendUserType);
    window.dispatchEvent(new Event("rbac:updated"));

    if (response?.employee?.id) {
      setAuthItem("userId", String(response.employee.id));
    } else if (response?.user?.id) {
      setAuthItem("userId", String(response.user.id));
    }
    const employeeId =
      response?.employee?.id ??
      response?.user?.employeeId ??
      response?.user?.employee?.id ??
      null;
    if (employeeId != null && employeeId !== "") {
      setAuthItem("employeeId", String(employeeId));
    }

    if (response.company) {
      setAuthJson("companyData", response.company);
    }

    onLogin(backendUserType);
  };

  const loginErrorMessage = (err) => {
    if (typeof err === "string") return err;
    if (err?.message === "Network Error") {
      return "Cannot reach the API server. Ensure the backend is running (npm run dev in backend folder), then restart the frontend.";
    }
    return err?.message || err?.response?.data?.message || "An error occurred during login";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (otpChallenge?.challengeId) {
        const response = await authService.verifyLogin2fa(otpChallenge.challengeId, otpCode);
        if (response.success) {
          setOtpChallenge(null);
          setOtpCode("");
          finishLogin(response);
        } else {
          setError(response.message || "Verification failed");
        }
        return;
      }

      const response = await authService.login(
        credentials.email,
        credentials.password
      );
      if (response.success && response.requires2fa) {
        setOtpChallenge({
          challengeId: response.challengeId,
          hint: response.hint || response.message,
        });
        setOtpCode("");
        return;
      }
      if (response.success) {
        finishLogin(response);
      } else {
        setError(response.message || "Login failed");
      }
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage("");
    setForgotPasswordLoading(true);

    try {
      const response = await authService.forgotPassword(forgotPasswordEmail);
      if (response.success) {
        setForgotPasswordMessage(response.message);
        setForgotPasswordEmail("");
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordMessage("");
        }, 3000);
      } else {
        setForgotPasswordMessage(response.message || "Failed to send reset email");
      }
    } catch (err) {
      setForgotPasswordMessage(err.message || "An error occurred");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgot = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordMessage("");
  };

  const inputCls =
    "h-11 w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-2.5 pl-10 pr-3 text-sm text-[hsl(var(--ds-fg))] outline-none transition placeholder:text-[hsl(var(--ds-muted-fg))] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  return (
    <div className="min-h-[100dvh] bg-[hsl(var(--ds-bg))] font-sans text-[hsl(var(--ds-fg))]">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-[1440px]">
        {/* Brand / product story */}
        <aside className="relative hidden w-[54%] overflow-hidden border-r border-[hsl(var(--ds-primary)/0.18)] lg:block">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(155deg, hsl(var(--ds-accent)) 0%, hsl(var(--ds-bg)) 42%, hsl(182 40% 94%) 100%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 18% 22%, hsl(var(--ds-primary) / 0.14) 0%, transparent 42%), radial-gradient(circle at 88% 78%, hsl(var(--ds-secondary) / 0.1) 0%, transparent 46%)",
            }}
          />

          <div className="relative flex h-full flex-col justify-between px-12 py-10 xl:px-16">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--ds-primary))]">
                Workforce platform
              </p>
              <h1 className="mt-4 max-w-[28rem] text-[2.65rem] font-bold leading-[1.08] tracking-tight text-[hsl(var(--ds-secondary))] xl:text-[3rem]">
                Modern HR management{" "}
                <span className="text-[hsl(var(--ds-primary))]">made simple</span>
              </h1>
              <p className="mt-4 max-w-[26rem] text-sm leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                Streamline workforce operations with intelligent automation — attendance,
                payroll, people actions, and more in one place.
              </p>

              <div className="mt-8 grid max-w-md grid-cols-3 gap-2.5">
                {STATS.map(([value, label]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-[hsl(var(--ds-primary)/0.2)] bg-white/90 px-3 py-3 shadow-[var(--ds-shadow-card)] backdrop-blur-sm"
                  >
                    <div className="text-xl font-bold tracking-tight text-[hsl(var(--ds-primary))]">
                      {value}
                    </div>
                    <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      {label}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid max-w-xl grid-cols-2 gap-2.5">
                {FEATURES.map(({ title, blurb, Icon }) => (
                  <div
                    key={title}
                    className="flex items-center gap-3 rounded-lg border border-[hsl(var(--ds-primary)/0.18)] bg-white/95 px-3 py-3 shadow-[var(--ds-shadow-card)]"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[hsl(var(--ds-primary)/0.08)] p-1">
                      <Icon size={36} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
                        {title}
                      </h3>
                      <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">{blurb}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="pt-8 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              {companyInfo?.name
                ? `Signed environment · ${companyInfo.name}`
                : "Paytiemp Smartech Solutions"}
            </p>
          </div>
        </aside>

        {/* Login panel */}
        <section className="flex w-full flex-col items-center justify-center px-5 py-10 lg:w-[46%] lg:px-10">
          <div className="w-full max-w-[420px]">
            <div className="mb-6 flex flex-col items-center text-center">
              <img
                src={paytiempLogo}
                alt="Paytiemp"
                className="h-14 w-auto object-contain sm:h-16"
              />
              <p className="mt-3 text-xs font-medium text-[hsl(var(--ds-muted-fg))] lg:hidden">
                Modern HR management made simple
              </p>
            </div>

            <div className="overflow-hidden rounded-lg border-2 border-[hsl(var(--ds-primary)/0.28)] bg-white shadow-[var(--ds-shadow-panel)]">
              <div className="border-b border-[hsl(var(--ds-primary)/0.12)] bg-[hsl(var(--ds-primary))] px-5 py-4 text-[hsl(var(--ds-primary-fg))]">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    Sign in
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-semibold">
                    <ShieldCheck size={12} strokeWidth={2} /> Secure
                  </span>
                </div>
                <h2 className="mt-3 text-xl font-bold tracking-tight">
                  {otpChallenge ? "Verify sign-in" : "Welcome back"}
                </h2>
                <p className="mt-0.5 text-xs text-white/85">
                  {otpChallenge
                    ? otpChallenge.hint || "Enter the code sent to your email."
                    : "Use your company email and password."}
                </p>
              </div>

              <div className="px-5 py-5">
                {error ? (
                  <div
                    role="alert"
                    className="mb-4 rounded-lg border border-[hsl(var(--ds-danger)/0.35)] bg-[hsl(var(--ds-danger)/0.08)] px-3 py-2.5 text-xs text-[hsl(var(--ds-danger))]"
                  >
                    {error}
                  </div>
                ) : null}

                <form onSubmit={handleSubmit} className="space-y-3.5">
                  {otpChallenge ? (
                    <div>
                      <label
                        htmlFor="login-otp"
                        className="mb-1 block text-xs font-semibold text-[hsl(var(--ds-fg))]"
                      >
                        Verification code <span className="text-[hsl(var(--ds-danger))]">*</span>
                      </label>
                      <input
                        id="login-otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className={inputCls}
                        placeholder="6-digit code"
                      />
                      <button
                        type="button"
                        className="mt-2 text-xs font-semibold text-[hsl(var(--ds-primary))]"
                        onClick={() => {
                          setOtpChallenge(null);
                          setOtpCode("");
                          setError("");
                        }}
                      >
                        Back to password
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label
                          htmlFor="login-email"
                          className="mb-1 block text-xs font-semibold text-[hsl(var(--ds-fg))]"
                        >
                          Email Address <span className="text-[hsl(var(--ds-danger))]">*</span>
                        </label>
                        <div className="relative">
                          <AtSign
                            size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
                          />
                          <input
                            id="login-email"
                            type="email"
                            name="username"
                            autoComplete="username"
                            required
                            value={credentials.email}
                            onChange={(e) =>
                              setCredentials({ ...credentials, email: e.target.value })
                            }
                            className={inputCls}
                            placeholder="your.email@company.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="login-password"
                          className="mb-1 block text-xs font-semibold text-[hsl(var(--ds-fg))]"
                        >
                          Password <span className="text-[hsl(var(--ds-danger))]">*</span>
                        </label>
                        <div className="relative">
                          <Lock
                            size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
                          />
                          <input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="current-password"
                            required
                            value={credentials.password}
                            onChange={(e) =>
                              setCredentials({ ...credentials, password: e.target.value })
                            }
                            className={`${inputCls} pr-10`}
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-primary)/0.08)] hover:text-[hsl(var(--ds-primary))]"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-0.5">
                        <label
                          htmlFor="remember-me"
                          className="flex cursor-pointer items-center gap-2 text-xs text-[hsl(var(--ds-muted-fg))]"
                        >
                          <input
                            id="remember-me"
                            name="remember-me"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] focus:ring-[hsl(var(--ds-ring))]"
                          />
                          Remember me
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-xs font-semibold text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary-hover))]"
                        >
                          Forgot password?
                        </button>
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[hsl(var(--ds-primary))] text-sm font-semibold text-[hsl(var(--ds-primary-fg))] transition hover:bg-[hsl(var(--ds-primary-hover))] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading
                      ? otpChallenge
                        ? "Verifying…"
                        : "Signing in…"
                      : otpChallenge
                        ? "Verify and sign in"
                        : "Sign In"}
                    {!loading ? <ArrowRight size={16} strokeWidth={2} /> : null}
                  </button>
                </form>
              </div>

              <div className="border-t border-[hsl(var(--ds-primary)/0.12)] bg-[hsl(var(--ds-primary)/0.03)] px-5 py-3 text-center text-[10px] leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                By signing in, you agree to our Terms of Service and Privacy Policy
                {companyInfo?.name ? ` · ${companyInfo.name}` : " · Paytiemp Smartech Solutions"}
              </div>
            </div>
          </div>
        </section>
      </div>

      {showForgotPassword ? (
        <div
          className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={closeForgot}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-lg border-2 border-[hsl(var(--ds-primary)/0.35)] bg-white shadow-[var(--ds-shadow-panel)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-password-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-primary)/0.15)] bg-[hsl(var(--ds-primary))] px-5 py-3.5 text-[hsl(var(--ds-primary-fg))]">
              <div>
                <h3 id="reset-password-title" className="text-base font-semibold tracking-tight">
                  Reset Password
                </h3>
                <p className="mt-0.5 text-xs text-white/85">
                  We will email a reset link for your account.
                </p>
              </div>
              <button
                type="button"
                onClick={closeForgot}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white/90 hover:bg-white/15"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4">
              {forgotPasswordMessage ? (
                <div
                  className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                    String(forgotPasswordMessage).toLowerCase().includes("success") ||
                    String(forgotPasswordMessage).toLowerCase().includes("sent")
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-[hsl(var(--ds-danger)/0.35)] bg-[hsl(var(--ds-danger)/0.08)] text-[hsl(var(--ds-danger))]"
                  }`}
                >
                  {forgotPasswordMessage}
                </div>
              ) : null}

              <form onSubmit={handleForgotPassword} className="space-y-3">
                <div>
                  <label
                    htmlFor="forgot-email"
                    className="mb-1 block text-xs font-semibold text-[hsl(var(--ds-fg))]"
                  >
                    Email Address
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    name="forgot-password-email"
                    autoComplete="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="h-11 w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                    placeholder="your.email@company.com"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={closeForgot}
                    className="h-10 flex-1 rounded-lg border border-[hsl(var(--ds-border))] text-sm font-semibold text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="h-10 flex-1 rounded-lg bg-[hsl(var(--ds-primary))] text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-60"
                  >
                    {forgotPasswordLoading ? "Sending…" : "Send Reset"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
